import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { CalendarPreference } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
} from '../../../../../../data-access';
import { AppSwitch, CustomTooltip, CustomTooltipContent } from '../../../../../common';
import { ReactComponent as ExternalCalSyncIcon } from '../../../../../../assets/tabs/settings-tab/external-cal-sync-icon.svg';
import { ReactComponent as CalendarSyncTooltipPreviewIcon } from '../../../../../../assets/tabs/settings-tab/external-cal-sync-icon.svg';
import { ReactComponent as QuestionIcon } from '../../../../../../assets/tabs/settings-tab/question-icon.svg';
import './ExternalCalendarSync.scss';

type UnSyncToCalendarType = NonNullable<CalendarPreference['unSyncToCalendar']>;

const DEFAULT_UNSYNC_TO_CALENDAR: UnSyncToCalendarType = {
  aiScheduler: false,
  dragAndDrop: false,
  task: false,
  routine: false,
  meal: false,
  sleep: false,
};

const EXTERNAL_CAL_SYNC_OPTIONS: { key: keyof UnSyncToCalendarType; labelKey: string }[] = [
  { key: 'aiScheduler', labelKey: 'settings.calendar.aiGeneratedEvents' },
  { key: 'dragAndDrop', labelKey: 'settings.calendar.dragAndDrop' },
  { key: 'task', labelKey: 'settings.calendar.autoScheduledTask' },
  { key: 'routine', labelKey: 'settings.calendar.autoScheduledRoutine' },
  { key: 'meal', labelKey: 'settings.aiAssistant.planTypes.meal' },
  { key: 'sleep', labelKey: 'settings.aiAssistant.planTypes.sleep' },
];

const EXTERNAL_CAL_SYNC_OPTION_TOOLTIP_KEYS: Partial<Record<keyof UnSyncToCalendarType, string>> = {
  aiScheduler: 'settings.calendar.externalCalendarSyncAiTooltip',
  dragAndDrop: 'settings.calendar.externalCalendarSyncDragTooltip',
  task: 'settings.calendar.externalCalendarSyncTaskTooltip',
  routine: 'settings.calendar.externalCalendarSyncRoutineTooltip',
  meal: 'settings.calendar.externalCalendarSyncMealTooltip',
  sleep: 'settings.calendar.externalCalendarSyncSleepTooltip',
};

const ExternalCalendarSync: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { calendarPreferences } = useCalendarContext();

  const [unSyncToCalendar, setUnSyncToCalendar] = useState<UnSyncToCalendarType>(
    DEFAULT_UNSYNC_TO_CALENDAR
  );

  useEffect(() => {
    setUnSyncToCalendar(calendarPreferences.unSyncToCalendar || DEFAULT_UNSYNC_TO_CALENDAR);
  }, [calendarPreferences.unSyncToCalendar]);

  const debouncedUpdateCalendarSettings = useMemo(
    () =>
      debounce((newData: Partial<CalendarPreference>) => {
        if (!user.preferenceId) {
          return;
        }

        return updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences: { ...newData },
        });
      }, 1000),
    [updateCalendarPref, user.preferenceId]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateCalendarSettings.cancel();
    };
  }, [debouncedUpdateCalendarSettings]);

  const handleUnSyncToCalendarChange = useCallback(
    (newValue: UnSyncToCalendarType) => {
      setUnSyncToCalendar(newValue);

      debouncedUpdateCalendarSettings({
        unSyncToCalendar: newValue,
      });
    },
    [debouncedUpdateCalendarSettings]
  );

  const handleOptionToggle = useCallback(
    (key: keyof UnSyncToCalendarType, checked: boolean) => {
      handleUnSyncToCalendarChange({
        ...unSyncToCalendar,
        [key]: !checked,
      });
    },
    [handleUnSyncToCalendarChange, unSyncToCalendar]
  );

  return (
    <Stack
      id="externalCalendarSync"
      gap={2}
      className="external-calendar-sync"
      sx={{ scrollMarginTop: '12px' }}
    >
      <Stack gap={1} direction="row" alignItems="center">
        <ExternalCalSyncIcon />
        <Typography variant="body1" className="external-calendar-sync__section-title">
          {t('settings.calendar.externalCalendarSync')}
        </Typography>
        <CustomTooltip
          title={
            <CustomTooltipContent
              title={t('settings.calendar.externalCalendarSyncTooltipTitle')}
              description={t('settings.calendar.externalCalendarSyncTooltipDescription')}
            >
              <CalendarSyncTooltipPreviewIcon className="external-calendar-sync__tooltip-preview-icon" />
            </CustomTooltipContent>
          }
          className="external-calendar-sync__tooltip"
        >
          <QuestionIcon />
        </CustomTooltip>
      </Stack>

      <Typography variant="subtitle2">
        {t('settings.calendar.externalCalendarSyncDescription')}
      </Typography>

      <Stack className="external-calendar-sync__list" gap={2}>
        {EXTERNAL_CAL_SYNC_OPTIONS.map((option, index) => {
          const optionTooltipKey = EXTERNAL_CAL_SYNC_OPTION_TOOLTIP_KEYS[option.key];

          return (
            <Stack key={option.key} className="external-calendar-sync__list-item-wrapper" gap={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className="external-calendar-sync__list-item"
              >
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography variant="body1" className="external-calendar-sync__list-item-title">
                    {t(option.labelKey)}
                  </Typography>
                  {optionTooltipKey && (
                    <CustomTooltip title={t(optionTooltipKey)}>
                      <QuestionIcon />
                    </CustomTooltip>
                  )}
                </Stack>

                <AppSwitch
                  checked={!unSyncToCalendar[option.key]}
                  onChange={(event) => handleOptionToggle(option.key, event.target.checked)}
                />
              </Stack>

              {index !== EXTERNAL_CAL_SYNC_OPTIONS.length - 1 && (
                <Divider className="external-calendar-sync__list-item-divider" />
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default ExternalCalendarSync;
